import { Controller } from '@hotwired/stimulus'

// Constants
const MARKER_ICON = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAABYlAAAWJQFJUiTwAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAHySURBVHgBtZY5TAJREIZn8bYwa2KnJnQelTYWNsZIrcbKTuk1Yke0cClMKE20Bzsrr5qINhZaYKWxgkTsTCAUeLvO/5yVl3AI7Polkx0e7Pzv/B9ENbBt2+TY4khypO0SOWkLcfipUfCSFKiXWDUho0LxNX5YHGahaNPh5Rtd33/Q3cMnZZ++1G9GBltooM9HM+NttDDZ7rya54gYhrFTVQDTIcUpkXqncKxIEKlFPwutznbqQhaLRMoEpOdKffvgmfYTr9QIEEEI685IDCnu50eKw9w9fSFEMywHOmhjsQsppmucRTI++Q5TY2K+my0O4jxqTC1qccTUCKT3aXyYDhfoURayWXq6DTqL9qgn04sRzCND790WB9gUR1xLCEFgDpkMzROueFsLUxAYQ+ZF7x1u+cwIfghgQdRB8gqts34f/TMQyCDBifSKUbYSIf8rMFJqdI3W2RtkF8gmhlrJK2CCwgkElGfArORwuAIuqxnfsY/9Ar5xjuLRYDe5ZaVkeHHdi4Ic+QAPbYkNq1k021Z3AxIlACWnYZPdULPduoGTau9FpGbZhWPRj7Mqb9pjZ83+ccKdqQ2UFhbFLedDpSszJCKmIwSfwul0TjsWcpi3NXaetjkqXpkVkUs/btdP0nbx7yIkBXJawbS0WRxmrRrfrURYg38xo54AAAAASUVORK5CYII='

const MAP_CONFIG = {
  STYLE: 'mapbox://styles/mapbox/dark-v11',
  // Keeps the marker/circle above the lens sheet, which is taller than it looks
  // once its two filter rows wrap on a narrow screen.
  PADDING: { bottom: 300 },
  ANIMATION_DURATION: 1000,
  ZOOM_STEP: 0.5,
  MIN_ZOOM: 1,
  MAX_ZOOM: 20,
  CIRCLE_SIZE_PERCENTAGE: 0.8,
  MIN_CIRCLE_SIZE: 20,
  EARTH_CIRCUMFERENCE: 40075017,
  TILE_SIZE: 512 // Mapbox GL JS uses 512x512 tiles
}

const RADIUS_CONFIG = {
  DEFAULT: 10,
  MIN: 1,
  MAX: 1000,
  STEPS: [
    { threshold: 10, step: 1 },
    { threshold: 20, step: 2 },
    { threshold: 50, step: 5 },
    { threshold: 100, step: 10 },
    { threshold: Infinity, step: 20 }
  ]
}

const CIRCLE_STYLES = {
  position: 'absolute',
  left: '50%',
  top: 'calc(50% - 156px)',
  transform: 'translate(-50%, -50%)',
  border: '2px solid #c4552a',
  backgroundColor: 'rgba(196, 85, 42, 0.15)',
  borderRadius: '50%',
  zIndex: '5',
  pointerEvents: 'none'
}

const MARKER_STYLES = {
  position: 'absolute',
  left: '50%',
  top: 'calc(50% - 150px)',
  transform: 'translate(-50%, -100%)',
  width: '12px',
  height: '12px',
  backgroundSize: '100%',
  zIndex: '10',
  pointerEvents: 'none'
}

export default class extends Controller {
  static targets = [
    'address', 'latitude', 'longitude', 'city', 'state', 'postalCode', 'country',
    'map', 'geocoderInput', 'radiusInput', 'radiusValue', 'useMyLocationButton'
  ]

  static values = {
    accessToken: String,
    isDefaultLocation: Boolean,
    // What to tell the user when the browser cannot place them — translated
    // by the ERB that mounts this controller (stimulus.geolocation).
    messages: Object
  }

  // Lifecycle Methods
  connect () {
    this.isAutoRadiusMode = true
    this.#initializeGeolocation()

    if (this.hasMapTarget) {
      this.#initMap()
      // Автоматически запрашиваем текущее местоположение при загрузке страницы
      this.#promptForCurrentLocation()
    }
  }

  disconnect () {
    this.#cleanup()
  }

  radiusInputTargetConnected () {
    this.#setupRadiusInputListener()
  }

  // Public API Methods
  handleCurrentLocation () {
    if (!navigator.geolocation) {
      this.#showError(this.messagesValue.unsupported)
      return
    }

    const options = {
      enableHighAccuracy: false,
      timeout: 10000,
      maximumAge: 300000 // 5 minutes
    }

    navigator.geolocation.getCurrentPosition(
      this.#handleGeolocationSuccess.bind(this),
      this.#handleGeolocationError.bind(this),
      options
    )
  }

  decreaseRadius () {
    this.#adjustZoom(MAP_CONFIG.ZOOM_STEP)
  }

  increaseRadius () {
    this.#adjustZoom(-MAP_CONFIG.ZOOM_STEP)
  }

  // Private Initialization Methods
  #initializeGeolocation () {
    if (!navigator.geolocation && this.hasUseMyLocationButtonTarget) {
      this.useMyLocationButtonTarget.remove()
    }
  }

  #promptForCurrentLocation () {
    // Если это дефолтная локация (пользователь еще не установил свою), запрашиваем геолокацию
    if (this.isDefaultLocationValue && navigator.geolocation) {
      // Небольшая задержка чтобы карта успела загрузиться
      setTimeout(() => {
        this.handleCurrentLocation()
      }, 500)
    }
  }

  #setupRadiusInputListener () {
    if (!this.hasRadiusInputTarget) return

    this.radiusInputTarget.addEventListener('input', this.#handleRadiusInput.bind(this))
  }

  // Event Handlers
  #handleRadiusInput () {
    this.isAutoRadiusMode = false
    this.#updateRadiusDisplay()
    this.#adjustZoomToRadius()
  }

  #handleGeolocationSuccess (position) {
    const { latitude, longitude } = position.coords
    this.#updateLocationAndMap(longitude, latitude)
  }

  #handleGeolocationError (error) {
    console.error('Geolocation error:', error)
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      https: window.location.protocol === 'https:',
      hostname: window.location.hostname
    })

    let errorMessage
    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = this.messagesValue.permission_denied
        break
      case error.POSITION_UNAVAILABLE:
        errorMessage = `${this.messagesValue.unavailable}\n\n${this.messagesValue.technical_details} ${error.message}`
        break
      case error.TIMEOUT:
        errorMessage = this.messagesValue.timeout
        break
      default:
        errorMessage = `${this.messagesValue.failed} Code: ${error.code}, Message: ${error.message}`
        break
    }

    this.#showError(errorMessage)
  }

  // UI Helper Methods
  #showError (message) {
    window.alert(message)
  }

  #updateRadiusDisplay () {
    const radius = this.#getCurrentRadius()
    if (this.hasRadiusValueTarget) {
      this.radiusValueTarget.textContent = radius
    }
  }

  #cleanup () {
    if (this.map) {
      this.map.remove()
      this.map = null
    }

    if (this.searchBox) {
      this.searchBox.remove()
      this.searchBox = null
    }

    if (this.radiusCircle?.parentNode) {
      this.radiusCircle.parentNode.removeChild(this.radiusCircle)
      this.radiusCircle = null
    }
  }

  // Zoom Control Methods
  #adjustZoom (zoomDelta) {
    if (!this.map) return

    const currentZoom = this.map.getZoom()
    const newZoom = Math.max(MAP_CONFIG.MIN_ZOOM, Math.min(MAP_CONFIG.MAX_ZOOM, currentZoom + zoomDelta))

    if (newZoom !== currentZoom) {
      this.map.zoomTo(newZoom)
    }
  }

  // Utility Methods
  #getCurrentRadius () {
    return parseFloat(this.radiusInputTarget?.value) || RADIUS_CONFIG.DEFAULT
  }

  #getRadiusStep (radius) {
    return RADIUS_CONFIG.STEPS.find(step => radius < step.threshold)?.step || RADIUS_CONFIG.STEPS[RADIUS_CONFIG.STEPS.length - 1].step
  }

  // Mathematical Calculation Methods
  #getMetersPerPixel (zoom, latitude) {
    // Mapbox GL JS uses 512x512 tiles (not 256x256), so we use TILE_SIZE instead of 2^8
    return (MAP_CONFIG.EARTH_CIRCUMFERENCE * Math.cos(latitude * Math.PI / 180)) / (MAP_CONFIG.TILE_SIZE * Math.pow(2, zoom))
  }

  #getTargetRadiusPixels () {
    const targetDiameter = this.mapTarget.offsetWidth * MAP_CONFIG.CIRCLE_SIZE_PERCENTAGE
    return targetDiameter / 2
  }

  #calculateOptimalRadius () {
    if (!this.map) return RADIUS_CONFIG.DEFAULT

    const zoom = this.map.getZoom()
    const center = this.map.getCenter()
    const targetRadiusPixels = this.#getTargetRadiusPixels()
    const metersPerPixel = this.#getMetersPerPixel(zoom, center.lat)
    const optimalRadiusKm = (targetRadiusPixels * metersPerPixel) / 1000

    // Use different precision based on radius size
    let roundedRadius
    if (optimalRadiusKm < 1) {
      roundedRadius = Math.round(optimalRadiusKm * 10) / 10 // Round to 1 decimal place
    } else {
      roundedRadius = Math.round(optimalRadiusKm)
    }

    return Math.max(RADIUS_CONFIG.MIN, Math.min(RADIUS_CONFIG.MAX, roundedRadius))
  }

  #calculateZoomFromRadius (latitude) {
    const targetRadius = this.#getCurrentRadius()
    const targetRadiusPixels = this.#getTargetRadiusPixels()
    const metersPerPixelNeeded = (targetRadius * 1000) / targetRadiusPixels
    const cosLat = Math.cos(latitude * Math.PI / 180)
    // Mapbox GL JS uses 512x512 tiles, so we use TILE_SIZE instead of 2^8
    const zoom = Math.log2((MAP_CONFIG.EARTH_CIRCUMFERENCE * cosLat) / (metersPerPixelNeeded * MAP_CONFIG.TILE_SIZE))

    return Math.max(MAP_CONFIG.MIN_ZOOM, Math.min(MAP_CONFIG.MAX_ZOOM, zoom))
  }

  // Circle Management Methods
  #updateRadiusCircle () {
    if (!this.radiusCircle || !this.map) return

    const radiusKm = this.#getCurrentRadius()
    const zoom = this.map.getZoom()
    const center = this.map.getCenter()
    const metersPerPixel = this.#getMetersPerPixel(zoom, center.lat)
    const radiusPixels = (radiusKm * 1000) / metersPerPixel
    const diameter = radiusPixels * 2

    Object.assign(this.radiusCircle.style, {
      maxWidth: '800px',
      maxHeight: '800px',
      width: `${diameter}px`,
      height: `${diameter}px`,
      opacity: diameter < MAP_CONFIG.MIN_CIRCLE_SIZE ? '0.8' : '1'
    })
  }

  #updateRadiusToFitZoom () {
    if (!this.isAutoRadiusMode) {
      this.#updateRadiusCircle()
      return
    }

    const optimalRadius = this.#calculateOptimalRadius()
    if (this.hasRadiusInputTarget) this.radiusInputTarget.value = optimalRadius
    this.#updateRadiusDisplay()
    this.#updateRadiusCircle()
  }

  #adjustZoomToRadius () {
    if (!this.map) return

    const center = this.map.getCenter()
    const targetZoom = this.#calculateZoomFromRadius(center.lat)
    this.map.zoomTo(targetZoom)
  }

  // Map Initialization Methods
  #initMap () {
    if (typeof window.mapboxgl === 'undefined') {
      console.warn('Mapbox GL JS not loaded yet, retrying...')
      setTimeout(() => this.#initMap(), 100)
      return
    }

    window.mapboxgl.accessToken = this.accessTokenValue

    const coordinates = this.#getInitialCoordinates()
    const initialZoom = this.#calculateZoomFromRadius(coordinates.latitude)

    this.map = new window.mapboxgl.Map({
      container: this.mapTarget,
      style: MAP_CONFIG.STYLE,
      center: [coordinates.longitude, coordinates.latitude],
      zoom: initialZoom
    })

    this.#setupMapBehavior()
    this.#createMapElements()
    this.#attachMapEventListeners()
  }

  #getInitialCoordinates () {
    return {
      latitude: parseFloat(this.latitudeTarget?.value) || 0,
      longitude: parseFloat(this.longitudeTarget?.value) || 0
    }
  }

  #setupMapBehavior () {
    this.map.easeTo({
      padding: MAP_CONFIG.PADDING,
      duration: MAP_CONFIG.ANIMATION_DURATION
    })

    // Initialize after map settles
    setTimeout(() => {
      this.#updateRadiusToFitZoom()
    }, MAP_CONFIG.ANIMATION_DURATION + 100)
  }

  #createMapElements () {
    this.mapTarget.style.position = 'relative'
    this.#createRadiusCircle()
    this.#createMarker()
  }

  #createRadiusCircle () {
    this.radiusCircle = document.createElement('div')
    this.radiusCircle.className = 'transition-all duration-700 ease-in-out'
    Object.assign(this.radiusCircle.style, CIRCLE_STYLES)
    this.mapTarget.appendChild(this.radiusCircle)
  }

  #createMarker () {
    const markerEl = document.createElement('div')
    Object.assign(markerEl.style, MARKER_STYLES)
    markerEl.style.backgroundImage = `url(${MARKER_ICON})`

    this.mapTarget.appendChild(markerEl)
    this.markerEl = markerEl
  }

  #attachMapEventListeners () {
    this.map.on('moveend', () => {
      const center = this.map.getCenter()
      this.#reverseGeocode(center.lng, center.lat)
    })

    this.map.on('zoomend', () => {
      this.#updateRadiusToFitZoom()
    })

    this.map.on('load', () => {
      this.#updateRadiusToFitZoom()
    })
  }

  // Location Management Methods
  async #updateLocationAndMap (longitude, latitude) {
    await this.#reverseGeocode(longitude, latitude)

    if (this.map) {
      const targetZoom = this.#calculateZoomFromRadius(latitude)
      this.map.flyTo({
        center: [longitude, latitude],
        zoom: targetZoom,
        padding: MAP_CONFIG.PADDING
      })

      setTimeout(() => {
        this.#updateRadiusToFitZoom()
      }, MAP_CONFIG.ANIMATION_DURATION + 100)
    }
  }

  async #reverseGeocode (lng, lat) {
    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${window.mapboxgl.accessToken}`
      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.features?.length > 0) {
        this.#updateLocationData(data.features[0], lat, lng)
      }
    } catch (error) {
      console.error('Geocoding error:', error)
    }
  }

  #updateLocationData (place, lat, lng) {
    if (this.hasAddressTarget) this.addressTarget.value = place.place_name
    if (this.hasLatitudeTarget) this.latitudeTarget.value = lat
    if (this.hasLongitudeTarget) this.longitudeTarget.value = lng

    this.#parseAddressComponents(place)
  }

  // Address Parsing Methods
  #parseAddressComponents (place) {
    const components = this.#extractAddressComponents(place.context || [])
    this.#updateAddressFields(components)
  }

  #extractAddressComponents (context) {
    const components = { city: '', state: '', postalCode: '', country: '' }

    context.forEach(component => {
      const { id, text } = component

      if (id.startsWith('place')) components.city = text
      else if (id.startsWith('region')) components.state = text
      else if (id.startsWith('postcode')) components.postalCode = text
      else if (id.startsWith('country')) components.country = text
    })

    return components
  }

  #updateAddressFields (components) {
    const fieldMap = {
      city: 'cityTarget',
      state: 'stateTarget',
      postalCode: 'postalCodeTarget',
      country: 'countryTarget'
    }

    Object.entries(fieldMap).forEach(([component, target]) => {
      if (this[`has${target.charAt(0).toUpperCase() + target.slice(1)}`]) {
        this[target].value = components[component]
      }
    })
  }

  // #initSearchBox () {
  //   const searchBox = new window.MapboxSearchBox()
  //   this.searchBox = searchBox

  //   searchBox.accessToken = this.accessTokenValue
  //   searchBox.placeholder = 'Tennisclub, Stadt oder Adresse eingeben…'
  //   searchBox.limit = 10
  //   searchBox.theme = {
  //     variables: {
  //       borderRadius: '6px',
  //       border: '1px solid #d4d4d4',
  //       colorBackground: '#ffffff',
  //       colorText: '#262626',
  //       colorPrimary: '#2563EF',
  //       unit: '14px',
  //       fontFamily: 'inherit',
  //       boxShadow: 'none'
  //     }
  //   }

  //   searchBox.options = {
  //     // limit: 3,
  //     // poi_category: 'tennis,sports_center,park,stadium,gym,school,university,tennis_court,health,fitness,tennis_club,sports_complex,tennis_facility,tennis_courts,sports_field',
  //     // proximity: { lng: this.longitudeTarget.value, lat: this.latitudeTarget.value },
  //     // language: 'de',
  //     // rich_metadata_provider: 'mapbox',
  //     country: 'DE'
  //     // types: ['locality', 'neighborhood', 'street', 'address', 'place', 'address', 'poi'],
  //   }

  //   searchBox.marker = false
  //   searchBox.popoverOptions = {
  //     offset: 0
  //     // placement: 'top'
  //   }

  //   this.geocoderInputTarget.appendChild(searchBox)

  //   // Handle search result selection
  //   searchBox.addEventListener('retrieve', (event) => {
  //     const feature = event.detail.features[0]
  //     if (feature) {
  //       const [lng, lat] = feature.geometry.coordinates
  //       this.addressTarget.value = feature.properties.full_address || feature.properties.name
  //       this.latitudeTarget.value = lat
  //       this.longitudeTarget.value = lng

  //       // Parse address components
  //       this.#parseSearchBoxResult(feature)

  //       // Update map if exists
  //       if (this.map) {
  //         this.map.flyTo({
  //           center: [lng, lat],
  //           padding: { bottom: 400 },
}
